import React, { useMemo } from 'react';
import { S3How } from '@milanlaser/mlcomponents';
import { processBackgroundAndGradientColorMap, processBackgroundColor, useImageKey } from '../../utils/builder.utils';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { useFirebaseMetrics } from '../../utils/useFirebaseMetrics';

type S3BuilderProps = Omit<React.ComponentProps<typeof S3How>, 'images' | 'buttons'> & {
    images: Array<{
        image: string;
        title: string;
        description: string;
        imageAltText: string;
    }>;
    buttons: Array<{
        label: string;
        variant: 'primary' | 'secondary' | 'cta';
        url: string;
    }>;
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
};

export function S3HowSection(props: S3BuilderProps) {
    const { replaceVariables } = useFirebaseMetrics();
    const {
        images: originalImages,
        buttons,
        text1,
        text2,
        textHorizontalPosition,
        textVerticalPosition,
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        ...otherProps
    } = props;

    const images = originalImages?.map((image) => ({
        src: image.image,
        alt: image.title || '', //alt is something, we have to keep because somehow the types being updated on MLComponents side are not reflecting here.
        ...image,
    }));

    const transformedButtons =
        buttons?.map((btn) => ({
            ...btn,
            onClick: () => (window.location.href = btn.url),
            target: '_self',
        })) || [];

    const processedText1 = typeof text1 === 'string' ? replaceVariables(text1) : text1;
    const processedText2 = typeof text2 === 'string' ? replaceVariables(text2) : text2;

    const sectionKey = useMemo(() => useImageKey(images), [images]);

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const modifiedProps = {
        ...otherProps,
        text1: processedText1,
        text2: processedText2,
        backgroundGradient: processBackgroundColor({ backgroundGradient: otherProps.backgroundGradient }),
        backgroundColor: processBackgroundColor({ backgroundColor: otherProps.backgroundColor }),
        className: `mcl-my-0 mcl-mx-auto mcl-max-w-7xl ${topPaddingClasses} ${bottomPaddingClasses}`,
    };

    //get the background color and gradient color for the wrapper div in builder.
    const backgroundColor = processBackgroundAndGradientColorMap({
        backgroundColor: otherProps.backgroundColor || 'default',
        gradientColor: otherProps.backgroundGradient || 'default',
    });

    return (
        <div className={`${backgroundColor}`}>
            <S3How
                key={sectionKey}
                {...modifiedProps}
                images={images}
                buttons={transformedButtons}
                textHorizontalPosition={textHorizontalPosition ? 'right' : 'left'}
                textVerticalPosition={textVerticalPosition ? 'bottom' : 'top'}
            />
        </div>
    );
}
